import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { LIVESTOCK_UNIT_CONVERSION } from "model/animalGroupTypes";
import { useCurrentFarm } from "model/useFarmQueries";
import { useMemo } from "react";
import { getFeedBudgetPlan, listFeedBudgetPlans } from "services";

interface MonthEntry {
  month: number;
  yearOffset?: number;
  animalCount: number;
  averageWeightKg: number;
  allocationPercent: number;
}

export interface StockClassPlan {
  defaultStockClassName?: string;
  months: MonthEntry[];
}

export interface ActiveGroupMetrics {
  /** Daily feed demand (kgDM/day) for the current plan month. */
  demandKgDay: number;
  animalCount: number;
  livestockUnits: number;
}

function currentEntry(
  months: MonthEntry[],
  month: number
): MonthEntry | undefined {
  return (
    months.find((m) => m.month === month && (m.yearOffset ?? 0) === 0) ??
    months.find((m) => m.month === month)
  );
}

export function computeGroupMetrics(
  agb: { stockClassPlans: StockClassPlan[] },
  month: number
): ActiveGroupMetrics {
  let demandKgDay = 0;
  let animalCount = 0;
  let livestockUnits = 0;
  for (const sc of agb.stockClassPlans) {
    const entry = currentEntry(sc.months, month);
    if (!entry) continue;
    demandKgDay +=
      ((entry.averageWeightKg * entry.allocationPercent) / 100) *
      entry.animalCount;
    animalCount += entry.animalCount;
    livestockUnits +=
      entry.animalCount *
      (LIVESTOCK_UNIT_CONVERSION[sc.defaultStockClassName ?? ""] ?? 0.5);
  }
  return { demandKgDay, animalCount, livestockUnits };
}

/**
 * Loads the farm's active feed budget and exposes per-group metrics
 * (demand, animal count, livestock units) for the current plan month.
 *
 * The feed budget is the single source of truth for animal numbers and demand —
 * callers should read from here, not from the backend StockClass records.
 * Returns an empty map when there is no active plan.
 */
export function useActiveFeedBudgetGroupMetrics(): Map<
  string,
  ActiveGroupMetrics
> {
  const { data: farm } = useCurrentFarm();
  const farmGuid = farm?.guid;

  const { data: planList } = useQuery({
    queryKey: farmGuid
      ? queryKeys.feedBudgetPlans.list(farmGuid)
      : ["feedBudgetPlans", "list", ""],
    queryFn: async () => {
      if (!farmGuid) throw new Error("Farm GUID required");
      return listFeedBudgetPlans(farmGuid);
    },
    enabled: !!farmGuid,
    staleTime: 5 * 60 * 1000,
  });

  const activePlan = planList?.find((p) => p.isActive) ?? null;
  const activePlanId = activePlan?.guid ?? null;

  const { data: fullPlan } = useQuery({
    queryKey: activePlanId
      ? queryKeys.feedBudgetPlans.detail(activePlanId)
      : ["feedBudgetPlans", "detail", ""],
    queryFn: async () => {
      if (!activePlanId) throw new Error("Plan ID required");
      return getFeedBudgetPlan(activePlanId);
    },
    enabled: !!activePlanId,
    staleTime: 5 * 60 * 1000,
  });

  // Entries use calendar months (January = 0), regardless of plan start date.
  const month = new Date().getMonth();

  return useMemo(() => {
    const result = new Map<string, ActiveGroupMetrics>();
    if (!fullPlan) return result;
    for (const agb of fullPlan.animalGroupBudgets) {
      result.set(agb.animalGroupId, computeGroupMetrics(agb, month));
    }
    return result;
  }, [fullPlan, month]);
}

/**
 * Convenience wrapper: per-group daily demand (kgDM/day) from the active plan.
 */
export function useActiveFeedBudgetDemand(): Map<string, number> {
  const metrics = useActiveFeedBudgetGroupMetrics();
  return useMemo(
    () => new Map([...metrics].map(([id, m]) => [id, m.demandKgDay])),
    [metrics]
  );
}
